import api from "api";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";
import { supportApiBase } from "api/hooks/useHCPs";
import { HcoSearchCategory, HcoSearchParams } from "../components/SearchHcoForm";

export interface HealthCareOrganization {
  id: number;
  name: string;
  status: string;
  contact_email: string | null;
  billing_email: string | null;
  iana_timezone: string | null;
  organization_size_range: string | null;
  platform_fee_model: string | null;
  platform_fee_cents: number | null;
  platform_fee_bps: number | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  stripe_subscription_status: string | null;
  address: string | null;
  city: string | null;
  zip_code: string | null;
  country_id: number | null;
  us_state_id: number | null;
  seats: number;
  created_at: string | null;
  updated_at: string | null;
}

export const useHealthCareOrganizations = () => {
  const key = queryKeys.healthCareOrganizations();
  return useQuery(key, async () => {
    const response = await api.call("/health_care_organizations?limit=250", { root: supportApiBase });
    const data = (await response.json()) as HcoListResponse;
    return data.items;
  });
};

export const useHealthCareOrganization = (id: number) => {
  const key = queryKeys.healthCareOrganization(id);
  return useQuery(key, async () => {
    const response = await api.call(`/health_care_organizations/${id}`, { root: supportApiBase });
    return response.json() as Promise<HealthCareOrganization>;
  });
};

export interface HcoListItem {
  id: number;
  name: string;
}

export interface HcoListResponse {
  items: HcoListItem[];
  metadata: {
    page: number;
    next: number | null;
    prev: number | null;
    page_items: number;
    total_count: number;
  };
}

export const useSearchHealthCareOrganizations = (params: HcoSearchParams) => {
  const { category, search, country } = params;
  const key = queryKeys.hcoSearch(params);

  return useQuery(
    key,
    async () => {
      const queryParams = new URLSearchParams();
      if (category === HcoSearchCategory.id) {
        queryParams.set("id", search);
      } else {
        queryParams.set("search", search);
      }
      if (country) {
        queryParams.set("country", country);
      }
      const response = await api.call(`/health_care_organizations?${queryParams}`, { root: supportApiBase });
      return response.json() as Promise<HcoListResponse>;
    },
    { enabled: !!search }
  );
};

export interface CountryItem {
  id: number;
  name: string;
  country_code: string;
}

export const useCountries = () => {
  const key = queryKeys.countries();
  return useQuery(key, async () => {
    const response = await api.call("/countries", { root: supportApiBase });
    return response.json() as Promise<CountryItem[]>;
  });
};

export interface CreateHealthCareOrganizationInput {
  name: string;
  contact_email?: string;
  billing_email?: string;
  iana_timezone?: string;
  organization_size_range?: string;
  platform_fee_model?: string;
  platform_fee_cents?: number;
  platform_fee_bps?: number;
  address?: string;
  city?: string;
  zip_code?: string;
  country_id?: number;
  us_state_id?: number;
  seats?: number;
}

export const useCreateHealthCareOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: CreateHealthCareOrganizationInput) => {
      const response = await api.call("/health_care_organizations", {
        method: "POST",
        data,
        root: supportApiBase,
      });
      return response.json() as Promise<HealthCareOrganization>;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.healthCareOrganizations());
        queryClient.invalidateQueries(queryKeys.hcoSearch());
      },
    }
  );
};

export interface UpdateHealthCareOrganizationInput {
  id: number;
  name?: string;
  status?: string;
  contact_email?: string | null;
  billing_email?: string | null;
  iana_timezone?: string | null;
  organization_size_range?: string | null;
  platform_fee_model?: string | null;
  platform_fee_cents?: number | null;
  platform_fee_bps?: number | null;
  address?: string | null;
  city?: string | null;
  zip_code?: string | null;
  country_id?: number | null;
  us_state_id?: number | null;
  seats?: number;
}

export interface StripePrice {
  id: string;
  currency: string;
  nickname: string;
  unit_amount: number;
  recurring_interval: string;
  recurring_interval_count: number;
}

export const useStripePrices = (hcoId: number, enabled: boolean) => {
  return useQuery(
    ["stripePrices", hcoId],
    async () => {
      const response = await api.call(`/health_care_organizations/${hcoId}/subscription/seats/prices`, {
        root: supportApiBase,
      });
      return response.json() as Promise<StripePrice[]>;
    },
    { enabled }
  );
};

export interface CreateStripeSubscriptionInput {
  hcoId: number;
  price_id?: string;
  price_cents?: number;
}

export const useCreateStripeSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ hcoId, ...data }: CreateStripeSubscriptionInput) => {
      await api.call(`/health_care_organizations/${hcoId}/subscription`, {
        method: "POST",
        data,
        root: supportApiBase,
      });
    },
    {
      onSuccess: (_, { hcoId }) => {
        queryClient.invalidateQueries(queryKeys.healthCareOrganization(hcoId));
      },
    }
  );
};

export interface UpdateStripeSubscriptionInput {
  hcoId: number;
  price_id?: string;
  price_cents?: number;
  seats?: number;
}

export const useUpdateStripeSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ hcoId, ...data }: UpdateStripeSubscriptionInput) => {
      await api.call(`/health_care_organizations/${hcoId}/subscription`, {
        method: "PUT",
        data,
        root: supportApiBase,
      });
    },
    {
      onSuccess: (_, { hcoId }) => {
        queryClient.invalidateQueries(queryKeys.healthCareOrganization(hcoId));
      },
    }
  );
};

export const useCancelStripeSubscription = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (hcoId: number) => {
      await api.call(`/health_care_organizations/${hcoId}/subscription`, {
        method: "DELETE",
        root: supportApiBase,
      });
    },
    {
      onSuccess: (_, hcoId) => {
        queryClient.invalidateQueries(queryKeys.healthCareOrganization(hcoId));
      },
    }
  );
};

export const useUpdateHealthCareOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, ...data }: UpdateHealthCareOrganizationInput) => {
      await api.call(`/health_care_organizations/${id}`, {
        method: "PUT",
        data,
        root: supportApiBase,
      });
      return id;
    },
    {
      onSuccess: id => {
        queryClient.invalidateQueries(queryKeys.healthCareOrganizations());
        queryClient.invalidateQueries(queryKeys.healthCareOrganization(id));
      },
    }
  );
};
